import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Modal } from './Modal.jsx';

function Icon({ name, size = 18 }) {
  const paths = {
    qr: <><rect x="4" y="4" width="6" height="6" /><rect x="14" y="4" width="6" height="6" /><rect x="4" y="14" width="6" height="6" /><path d="M14 14h3v3h3M14 20v-3M20 14h-3" /></>,
    download: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M5 20h14" /></>,
    copy: <><rect x="9" y="9" width="10" height="10" rx="2" /><path d="M15 9V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /></>,
    external: <><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" /></>,
    close: <path d="M6 6l12 12M18 6 6 18" />,
  };
  return <svg className="ui-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

export function MenuQrCode({ menuName, menuSlug, menuUrl }) {
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const copyButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError(null);
    setCopied(false);
    setCopyError(null);
    QRCode.toDataURL(menuUrl, {
      width: 720,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#17251b', light: '#ffffff' },
    })
      .then((result) => {
        if (!cancelled) setDataUrl(result);
      })
      .catch(() => {
        if (!cancelled) setError('Could not create the QR code. Please try again.');
      });
    return () => { cancelled = true; };
  }, [menuUrl, open]);

  async function handleCopy() {
    setCopyError(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(menuUrl);
      } else {
        const field = document.createElement('textarea');
        field.value = menuUrl;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        let success = false;
        try {
          field.select();
          success = document.execCommand('copy');
        } finally {
          field.remove();
          copyButtonRef.current?.focus({ preventScroll: true });
        }
        if (!success) throw new Error('Copy was not available');
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
      setCopyError('Could not copy the link. Select and copy it from the field above.');
    }
  }

  function handleDownload() {
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${menuSlug || 'menu'}-qr-code.png`;
    link.click();
  }

  return (
    <>
      <button type="button" className="btn btn-sm qr-trigger" onClick={() => setOpen(true)} aria-label={`Show QR code for ${menuName}`}>
        <Icon name="qr" size={16} /> QR code
      </button>

      {open && (
        <Modal onClose={() => setOpen(false)} className="qr-modal" backdropClassName="qr-modal-backdrop" labelledBy="qr-modal-title">
          <header className="qr-modal__header">
            <div><span className="qr-modal__eyebrow">Share your menu</span><h2 id="qr-modal-title">{menuName}</h2></div>
            <button type="button" className="item-editor__close" onClick={() => setOpen(false)} aria-label="Close QR code dialog"><Icon name="close" size={21} /></button>
          </header>
          <div className="qr-modal__body">
            <p className="qr-modal__intro">Guests can scan this code with their phone camera to open your menu instantly.</p>
            <div className="qr-code-frame">
              {error ? <div className="alert alert-error">{error}</div> : dataUrl ? <img src={dataUrl} alt={`QR code for ${menuName}`} /> : <span className="spinner" aria-label="Creating QR code" />}
            </div>
            <label className="qr-modal__url-label" htmlFor="public-menu-url">Public menu link</label>
            <div className="qr-modal__url-row"><input id="public-menu-url" type="text" readOnly value={menuUrl} /><button ref={copyButtonRef} type="button" className="btn btn-sm" onClick={handleCopy}><Icon name="copy" size={15} /> {copied ? 'Copied' : 'Copy link'}</button></div>
            {copyError && <p className="field-error" role="alert">{copyError}</p>}
            <p className="qr-modal__hint">Download this image for table cards, counter signs, menus, or receipts.</p>
          </div>
          <footer className="qr-modal__footer">
            <a className="btn btn-ghost" href={menuUrl} target="_blank" rel="noreferrer"><Icon name="external" size={16} /> Open menu</a>
            <button type="button" className="btn btn-primary" onClick={handleDownload} disabled={!dataUrl}><Icon name="download" size={16} /> {dataUrl ? 'Download QR code' : 'Preparing QR code…'}</button>
          </footer>
        </Modal>
      )}
    </>
  );
}
